import React, { useMemo } from "react";
import { Stack, Text, TooltipHost, Icon } from "@fluentui/react";
import { ICatalystTelemetryVsSentimentGraph } from "../../../../interfaces/Catalyst.interfaces";
import { CatalystTabTooltips } from "../../Common/HealthView.constants";

export interface TelemetryVsSentimentProps {
  data?: ICatalystTelemetryVsSentimentGraph[];
}

const KNOWN_COLORS: Record<string, string> = {
  green: "#73AA24",
  red: "#D77440",
  yellow: "#D0B232",
  blue: "#0078D4",
  orange: "#D77440",
  unknown: "#808080",
};

const FALLBACK_PALETTE = ["#8764B8", "#00B294", "#018574", "#D83B01", "#744DA9"];

interface BarSegment {
  label: string;
  value: number;
  color: string;
}

const StackedBar: React.FC<{ segments: BarSegment[]; height?: number }> = ({ segments, height = 32 }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        height,
        gap: 3,
      }}
    >
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <div
            key={s.label}
            style={{
              width: `${(s.value / total) * 100}%`,
              backgroundColor: s.color,
              borderRadius: 4,
            }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
    </div>
  );
};

export const CatalystTelemetryVsSentiment: React.FC<TelemetryVsSentimentProps> = ({
  data,
}) => {
  const { parameterGroups, colorLabels } = useMemo(() => {
    if (!data || data.length === 0) return { parameterGroups: [], colorLabels: [] };

    const groupMap = new Map<string, Map<string, number>>();
    const allColors = new Set<string>();
    let fallbackIdx = 0;
    const colorMap: Record<string, string> = {};

    data.forEach((item) => {
      const colorRaw = item.colorIndicator?.trim();
      const colorKey = colorRaw ? colorRaw.toLowerCase() : "unknown";

      const param = item.parameter ?? "Unknown";
      if (!groupMap.has(param)) {
        groupMap.set(param, new Map());
      }
      const paramMap = groupMap.get(param)!;
      paramMap.set(colorKey, (paramMap.get(colorKey) ?? 0) + (item.value ?? 0));
      allColors.add(colorKey);

      if (!colorMap[colorKey]) {
        colorMap[colorKey] = KNOWN_COLORS[colorKey] ?? FALLBACK_PALETTE[fallbackIdx++ % FALLBACK_PALETTE.length];
      }
    });

    const colorLabelsArr = Array.from(allColors).map((c) => ({
      label: c.charAt(0).toUpperCase() + c.slice(1),
      color: colorMap[c],
    }));

    const groups = Array.from(groupMap.entries()).map(([parameter, colorValues]) => {
      const segments: BarSegment[] = Array.from(colorValues.entries()).map(([colorKey, value]) => ({
        label: colorKey.charAt(0).toUpperCase() + colorKey.slice(1),
        value,
        color: colorMap[colorKey],
      }));
      return { parameter, segments };
    });

    return { parameterGroups: groups, colorLabels: colorLabelsArr };
  }, [data]);

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { flex: 1 } }}>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { marginBottom: 12 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
          Telemetry- Driven Health Score vs. Customer Sentiment
        </Text>
        <TooltipHost content={CatalystTabTooltips.TelemetryHeader}>
          <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
        </TooltipHost>
      </Stack>

      {parameterGroups.map((group) => {
        const tooltipText = CatalystTabTooltips.TelemetryItems[group.parameter];
        return (
          <Stack tokens={{ childrenGap: 4 }} key={group.parameter}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
              <Text variant="medium" styles={{ root: { fontWeight: 600, color: "#323130" } }}>
                {group.parameter}
              </Text>
              {tooltipText && (
                <TooltipHost content={tooltipText}>
                  <Icon iconName="Info" styles={{ root: { fontSize: 12, color: "#605E5C", cursor: "pointer" } }} />
                </TooltipHost>
              )}
            </Stack>
            <StackedBar segments={group.segments} />
          </Stack>
        );
      })}

      {/* Legend */}
      <Stack horizontal tokens={{ childrenGap: 16 }} horizontalAlign="center" verticalAlign="center">
        {colorLabels.map((item) => (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} key={item.label}>
            <div style={{ width: 12, height: 12, backgroundColor: item.color, borderRadius: 2 }} />
            <Text variant="small">{item.label}</Text>
          </Stack>
        ))}
        {colorLabels.length > 0 && (
          <TooltipHost content={CatalystTabTooltips.TelemetryLegend}>
            <Icon iconName="Info" styles={{ root: { fontSize: 12, color: "#605E5C", cursor: "pointer" } }} />
          </TooltipHost>
        )}
      </Stack>
    </Stack>
  );
};
